import React, { useState, useRef, useCallback, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useQueryState, parseAsBoolean } from "nuqs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MergeFieldSelector } from "@/components/MergeFieldSelector";
import { useToast } from "@/hooks/use-toast";
import { 
  Type, 
  Image, 
  Link, 
  Square, 
  Trash2, 
  Move, 
  Eye, 
  Code, 
  Save,
  Plus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline,
  Tag
} from "lucide-react";

interface EmailComponent {
  id: string;
  type: "text" | "heading" | "image" | "button" | "spacer" | "divider";
  content: string;
  styles: {
    fontSize?: string;
    fontWeight?: string;
    textAlign?: "left" | "center" | "right";
    color?: string;
    backgroundColor?: string;
    padding?: string;
    margin?: string;
    borderRadius?: string;
    textDecoration?: string;
    fontStyle?: string;
  };
  properties?: {
    src?: string;
    alt?: string;
    href?: string;
    height?: string;
    width?: string;
  };
}

interface EmailDragDropEditorProps {
  initialTemplate?: {
    name: string;
    subject: string;
    htmlContent: string;
    textContent: string;
    componentStructure?: EmailComponent[];
  };
  onSave: (template: {
    name: string;
    subject: string;
    htmlContent: string;
    textContent: string;
    componentStructure?: EmailComponent[];
  }) => void;
  onCancel: () => void;
}

const componentPalette = [
  { type: "heading", icon: Type, label: "Heading", defaultContent: "Your Heading Here" },
  { type: "text", icon: Type, label: "Text Block", defaultContent: "Add your text content here..." },
  { type: "image", icon: Image, label: "Image", defaultContent: "" },
  { type: "button", icon: Link, label: "Button", defaultContent: "Click Here" },
  { type: "spacer", icon: Square, label: "Spacer", defaultContent: "" },
  { type: "divider", icon: Square, label: "Divider", defaultContent: "" },
];

export default function EmailDragDropEditor({ 
  initialTemplate, 
  onSave, 
  onCancel 
}: EmailDragDropEditorProps) {
  const { toast } = useToast();
  const [templateName, setTemplateName] = useState(initialTemplate?.name || "");
  const [templateSubject, setTemplateSubject] = useState(initialTemplate?.subject || "");
  const [components, setComponents] = useState<EmailComponent[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);
  // NUQS: URL-based state for preview mode (shareable preview state)
  const [previewMode, setPreviewMode] = useQueryState("preview", parseAsBoolean.withDefault(false));
  const [showMergeFieldSelector, setShowMergeFieldSelector] = useState(false);
  const [activeField, setActiveField] = useState<'subject' | 'content' | null>(null);
  const dragCounter = useRef(0);
  
  // Refs for merge field insertion
  const subjectRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  const generateId = () => `component_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Load component structure from initialTemplate (preferred) or parse HTML (fallback)
  useEffect(() => {
    if (initialTemplate && components.length === 0) {
      // Prefer component_structure if available (perfect editing)
      if (initialTemplate.componentStructure && Array.isArray(initialTemplate.componentStructure)) {
        setComponents(initialTemplate.componentStructure);
        return;
      }
      
      // Fallback: Parse HTML content into components (imperfect)
      if (initialTemplate.htmlContent) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(initialTemplate.htmlContent, 'text/html');
        const body = doc.querySelector('body');
        const parsedComponents: EmailComponent[] = [];
        
        if (body) {
          body.childNodes.forEach((node, index) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              const tagName = element.tagName.toLowerCase();
              
              if (tagName === 'h2') {
                parsedComponents.push({
                  id: `parsed_heading_${index}`,
                  type: 'heading',
                  content: element.textContent || '',
                  styles: {
                    fontSize: "24px",
                    fontWeight: "bold",
                    textAlign: "left",
                    color: "#333333",
                    padding: "16px",
                    margin: "8px 0",
                  },
                  properties: {}
                });
              } else if (tagName === 'p') {
                parsedComponents.push({
                  id: `parsed_text_${index}`,
                  type: 'text',
                  content: element.textContent || '',
                  styles: {
                    fontSize: "16px",
                    textAlign: "left",
                    color: "#666666",
                    padding: "16px",
                    margin: "8px 0",
                  },
                  properties: {}
                });
              } else if (tagName === 'div' && element.querySelector('a')) {
                const link = element.querySelector('a');
                parsedComponents.push({
                  id: `parsed_button_${index}`,
                  type: 'button',
                  content: link?.textContent || '',
                  styles: {
                    fontSize: "16px",
                    textAlign: "center",
                    color: "#ffffff",
                    backgroundColor: "#007cba",
                    padding: "12px 24px",
                    margin: "16px 0",
                    borderRadius: "4px",
                  },
                  properties: {
                    href: link?.getAttribute('href') || '#'
                  }
                });
              }
            }
          });
        }
        
        // If no components were parsed but we have text content, create a text component
        if (parsedComponents.length === 0 && initialTemplate.textContent) {
          parsedComponents.push({
            id: 'parsed_text_fallback',
            type: 'text',
            content: initialTemplate.textContent,
            styles: {
              fontSize: "16px",
              textAlign: "left",
              color: "#666666",
              padding: "16px",
              margin: "8px 0",
            },
            properties: {}
          });
        }
        
        setComponents(parsedComponents);
      }
    }
  }, [initialTemplate]);

  // Helper function to insert merge field at cursor position
  const insertMergeField = (tag: string, ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement>, stateUpdater: (value: string) => void) => {
    const element = ref.current;
    if (!element) return;

    const start = element.selectionStart || 0;
    const end = element.selectionEnd || 0;
    const currentValue = element.value;
    
    const newValue = currentValue.substring(0, start) + tag + currentValue.substring(end);
    
    // Update the state
    stateUpdater(newValue);
    
    // Set cursor position after the inserted tag
    setTimeout(() => {
      element.focus();
      element.setSelectionRange(start + tag.length, start + tag.length);
    }, 0);
  };

  const createComponent = (type: EmailComponent["type"]): EmailComponent => {
    const baseComponent = {
      id: generateId(),
      type,
      content: componentPalette.find(p => p.type === type)?.defaultContent || "",
      styles: {
        padding: "16px",
        margin: "8px 0",
      },
      properties: {},
    };

    switch (type) {
      case "heading":
        return {
          ...baseComponent,
          styles: {
            ...baseComponent.styles,
            fontSize: "24px",
            fontWeight: "bold",
            textAlign: "left",
            color: "#333333",
          },
        };
      case "text":
        return {
          ...baseComponent,
          styles: {
            ...baseComponent.styles,
            fontSize: "16px",
            textAlign: "left",
            color: "#666666",
          },
        };
      case "button":
        return {
          ...baseComponent,
          styles: {
            ...baseComponent.styles,
            backgroundColor: "#007bff",
            color: "#ffffff",
            padding: "12px 24px",
            borderRadius: "4px",
            textAlign: "center",
            textDecoration: "none",
            fontWeight: "bold",
          },
          properties: {
            href: "#",
          },
        };
      case "image":
        return {
          ...baseComponent,
          properties: {
            src: "https://via.placeholder.com/600x200",
            alt: "Image description",
            width: "100%",
          },
        };
      case "spacer":
        return {
          ...baseComponent,
          styles: {
            ...baseComponent.styles,
            height: "40px",
          },
        };
      case "divider":
        return {
          ...baseComponent,
          styles: {
            ...baseComponent.styles,
            height: "1px",
            backgroundColor: "#dddddd",
            margin: "20px 0",
          },
        };
      default:
        return baseComponent;
    }
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    if (result.source.droppableId === "palette" && result.destination.droppableId === "canvas") {
      // Adding new component from palette
      const componentType = result.draggableId as EmailComponent["type"];
      const newComponent = createComponent(componentType);
      const newComponents = [...components];
      newComponents.splice(result.destination.index, 0, newComponent);
      setComponents(newComponents);
    } else if (result.source.droppableId === "canvas" && result.destination.droppableId === "canvas") {
      // Reordering existing components
      const newComponents = Array.from(components);
      const [reorderedItem] = newComponents.splice(result.source.index, 1);
      newComponents.splice(result.destination.index, 0, reorderedItem);
      setComponents(newComponents);
    }
  };

  const updateComponent = (id: string, updates: Partial<EmailComponent>) => {
    setComponents(prev => prev.map(comp => 
      comp.id === id ? { ...comp, ...updates } : comp
    ));
  };

  const deleteComponent = (id: string) => {
    setComponents(prev => prev.filter(comp => comp.id !== id));
    if (selectedComponent === id) {
      setSelectedComponent(null);
    }
  };

  const generateHTML = useCallback(() => {
    const componentHTML = components.map(comp => {
      const styleString = Object.entries(comp.styles)
        .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
        .join('; ');

      switch (comp.type) {
        case "heading":
          return `<h2 style="${styleString}">${comp.content.replace(/\n/g, '<br>')}</h2>`;
        case "text":
          return `<p style="${styleString}">${comp.content.replace(/\n/g, '<br>')}</p>`;
        case "button":
          return `<div style="text-align: ${comp.styles.textAlign || 'center'}; margin: ${comp.styles.margin || '16px 0'};">
            <a href="${comp.properties?.href || '#'}" style="${styleString}; display: inline-block;">${comp.content}</a>
          </div>`;
        case "image":
          return `<div style="text-align: center; margin: ${comp.styles.margin || '16px 0'};">
            <img src="${comp.properties?.src || ''}" alt="${comp.properties?.alt || ''}" style="max-width: 100%; height: auto; width: ${comp.properties?.width || '100%'};" />
          </div>`;
        case "spacer":
          return `<div style="height: ${comp.styles.height || '40px'}; margin: ${comp.styles.margin || '0'};"></div>`;
        case "divider":
          return `<hr style="${styleString}; border: none;" />`;
        default:
          return "";
      }
    }).join('\n');

    return `
      <html>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          ${componentHTML}
        </body>
      </html>
    `;
  }, [components]);

  const generateTextContent = useCallback(() => {
    return components
      .filter(comp => comp.type === "text" || comp.type === "heading")
      .map(comp => comp.content)
      .join('\n\n');
  }, [components]);

  const handleSave = () => {
    if (!templateName.trim() || !templateSubject.trim()) {
      toast({
        title: "Validation Error",
        description: "Please provide both template name and subject",
        variant: "destructive"
      });
      return;
    }

    const htmlContent = generateHTML();
    const textContent = generateTextContent();

    const templateData = {
      name: templateName,
      subject: templateSubject,
      htmlContent,
      textContent,
      componentStructure: components, // Save component structure for visual editor
    };
    
    onSave(templateData);
  };

  const selectedComp = selectedComponent ? components.find(c => c.id === selectedComponent) : null;

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <DragDropContext onDragEnd={handleDragEnd}>
        {/* Component Palette */}
        <div className="w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold dark:text-gray-100 mb-4">Components</h3>
          <Droppable droppableId="palette" isDropDisabled={true}>
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {componentPalette.map((component, index) => (
                  <Draggable
                    key={component.type}
                    draggableId={component.type}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-move hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-gray-200 ${
                          snapshot.isDragging ? "opacity-50" : ""
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <component.icon className="h-4 w-4" />
                          <span className="text-sm">{component.label}</span>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div>
                  <Label htmlFor="templateName">Template Name</Label>
                  <Input
                    id="templateName"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter template name"
                    className="w-48"
                  />
                </div>
                <div>
                  <Label htmlFor="templateSubject">Subject Line</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      ref={subjectRef}
                      id="templateSubject"
                      value={templateSubject}
                      onChange={(e) => setTemplateSubject(e.target.value)}
                      placeholder="Enter email subject"
                      className="w-64"
                    />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <MergeFieldSelector
                              onSelect={(tag) => insertMergeField(tag, subjectRef, setTemplateSubject)}
                              buttonText=""
                              buttonSize="sm"
                              buttonVariant="outline"
                              buttonIcon={<Tag className="h-4 w-4" />}
                              buttonClassName="h-10 px-3"
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Insert Merge Tags</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={previewMode ? "default" : "outline"}
                        onClick={() => setPreviewMode(!previewMode)}
                        size="sm"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Preview</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button onClick={handleSave} size="sm">
                        <Save className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Save Template</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 overflow-auto p-6">
            {previewMode ? (
              <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div dangerouslySetInnerHTML={{ __html: generateHTML() }} />
              </div>
            ) : (
              <Droppable droppableId="canvas">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 min-h-96"
                  >
                    {components.length === 0 && (
                      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                        <Type className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Drag components from the left panel to start building your email</p>
                      </div>
                    )}
                    {components.map((component, index) => (
                      <Draggable
                        key={component.id}
                        draggableId={component.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`relative group mb-2 ${
                              snapshot.isDragging ? "opacity-50" : ""
                            } ${
                              selectedComponent === component.id ? "ring-2 ring-blue-500" : ""
                            }`}
                            onClick={() => setSelectedComponent(component.id)}
                          >
                            <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="flex items-center space-x-1 bg-white dark:bg-gray-700 shadow-sm border dark:border-gray-600 rounded px-2 py-1">
                                <button
                                  {...provided.dragHandleProps}
                                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                                >
                                  <Move className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteComponent(component.id);
                                  }}
                                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-red-500"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                            {/* Component Render */}
                            <ComponentRenderer 
                              component={component} 
                              onUpdate={(updates) => updateComponent(component.id, updates)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </div>
        </div>

        {/* Properties Panel */}
        {selectedComp && !previewMode && (
          <div className="w-80 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 p-4">
            <ComponentPropertiesPanel
              component={selectedComp}
              onUpdate={(updates) => updateComponent(selectedComp.id, updates)}
            />
          </div>
        )}
      </DragDropContext>
    </div>
  );
}

// Component Renderer
function ComponentRenderer({ 
  component, 
  onUpdate 
}: { 
  component: EmailComponent; 
  onUpdate: (updates: Partial<EmailComponent>) => void;
}) {
  const styleObject = component.styles;

  switch (component.type) {
    case "heading":
      return (
        <h2 style={styleObject} className="outline-none">
          {component.content}
        </h2>
      );
    case "text":
      return (
        <p style={{ ...styleObject, whiteSpace: 'pre-wrap' }} className="outline-none">
          {component.content}
        </p>
      );
    case "button":
      return (
        <div style={{ textAlign: component.styles.textAlign || 'center', margin: component.styles.margin }}>
          <span style={styleObject} className="inline-block cursor-pointer hover:text-white transition-colors">
            {component.content}
          </span>
        </div>
      );
    case "image":
      return (
        <div style={{ textAlign: 'center', margin: component.styles.margin }}>
          <img 
            src={component.properties?.src || 'https://via.placeholder.com/600x200'} 
            alt={component.properties?.alt || ''} 
            style={{ maxWidth: '100%', height: 'auto', width: component.properties?.width }}
          />
        </div>
      );
    case "spacer":
      return (
        <div 
          style={{ 
            height: component.styles.height || '40px', 
            backgroundColor: 'rgba(0,0,0,0.05)',
            border: '1px dashed #ccc'
          }}
          className="flex items-center justify-center text-xs text-gray-500"
        >
          Spacer
        </div>
      );
    case "divider":
      return (
        <hr style={styleObject} />
      );
    default:
      return null;
  }
}

// Properties Panel
function ComponentPropertiesPanel({ 
  component, 
  onUpdate 
}: { 
  component: EmailComponent; 
  onUpdate: (updates: Partial<EmailComponent>) => void;
}) {
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);

  const updateContent = (content: string) => {
    onUpdate({ content });
  };

  // Helper function to insert merge field at cursor position in content
  const insertMergeFieldInContent = (tag: string) => {
    const element = contentTextareaRef.current;
    if (!element) return;

    const start = element.selectionStart || 0;
    const end = element.selectionEnd || 0;
    const currentValue = element.value;
    
    const newValue = currentValue.substring(0, start) + tag + currentValue.substring(end);
    
    // Update the content
    updateContent(newValue);
    
    // Set cursor position after the inserted tag
    setTimeout(() => {
      element.focus();
      element.setSelectionRange(start + tag.length, start + tag.length);
    }, 0);
  };

  const updateStyle = (styleKey: string, value: string) => {
    onUpdate({
      styles: {
        ...component.styles,
        [styleKey]: value,
      },
    });
  };

  const updateProperty = (propKey: string, value: string) => {
    onUpdate({
      properties: {
        ...component.properties,
        [propKey]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold mb-3 capitalize">{component.type} Properties</h3>
      </div>

      {/* Content */}
      {(component.type === "text" || component.type === "heading" || component.type === "button") && (
        <div>
          <Label>Content</Label>
          <Textarea
            ref={contentTextareaRef}
            value={component.content}
            onChange={(e) => updateContent(e.target.value)}
            rows={3}
          />
          <div className="mt-2">
            <MergeFieldSelector
              onSelect={insertMergeFieldInContent}
              buttonText="Insert Merge Tags"
              buttonSize="sm"
              buttonVariant="outline"
            />
          </div>
        </div>
      )}

      {/* Image Properties */}
      {component.type === "image" && (
        <div className="space-y-3">
          <div>
            <Label>Image URL</Label>
            <Input
              value={component.properties?.src || ''}
              onChange={(e) => updateProperty('src', e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div>
            <Label>Alt Text</Label>
            <Input
              value={component.properties?.alt || ''}
              onChange={(e) => updateProperty('alt', e.target.value)}
              placeholder="Image description"
            />
          </div>
          <div>
            <Label>Width</Label>
            <Input
              value={component.properties?.width || ''}
              onChange={(e) => updateProperty('width', e.target.value)}
              placeholder="100% or 300px"
            />
          </div>
        </div>
      )}

      {/* Button Properties */}
      {component.type === "button" && (
        <div>
          <Label>Link URL</Label>
          <Input
            value={component.properties?.href || ''}
            onChange={(e) => updateProperty('href', e.target.value)}
            placeholder="https://example.com"
          />
        </div>
      )}

      {/* Spacing Properties */}
      {component.type === "spacer" && (
        <div>
          <Label>Height</Label>
          <Input
            value={component.styles.height || ''}
            onChange={(e) => updateStyle('height', e.target.value)}
            placeholder="40px"
          />
        </div>
      )}

      {/* Text Styling */}
      {(component.type === "text" || component.type === "heading" || component.type === "button") && (
        <div className="space-y-3">
          <Separator />
          <h4 className="font-medium">Styling</h4>
          
          <div>
            <Label>Font Size</Label>
            <Input
              value={component.styles.fontSize || ''}
              onChange={(e) => updateStyle('fontSize', e.target.value)}
              placeholder="16px"
            />
          </div>

          <div>
            <Label>Text Color</Label>
            <Input
              type="color"
              value={component.styles.color || '#000000'}
              onChange={(e) => updateStyle('color', e.target.value)}
            />
          </div>

          <div>
            <Label>Text Alignment</Label>
            <Select 
              value={component.styles.textAlign || 'left'} 
              onValueChange={(value) => updateStyle('textAlign', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Font Weight</Label>
            <Select 
              value={component.styles.fontWeight || 'normal'} 
              onValueChange={(value) => updateStyle('fontWeight', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="bold">Bold</SelectItem>
                <SelectItem value="lighter">Light</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Background Color for Button */}
      {component.type === "button" && (
        <div>
          <Label>Background Color</Label>
          <Input
            type="color"
            value={component.styles.backgroundColor || '#007bff'}
            onChange={(e) => updateStyle('backgroundColor', e.target.value)}
          />
        </div>
      )}

      {/* Padding and Margin */}
      <div className="space-y-3">
        <Separator />
        <h4 className="font-medium">Spacing</h4>
        
        <div>
          <Label>Padding</Label>
          <Input
            value={component.styles.padding || ''}
            onChange={(e) => updateStyle('padding', e.target.value)}
            placeholder="16px"
          />
        </div>

        <div>
          <Label>Margin</Label>
          <Input
            value={component.styles.margin || ''}
            onChange={(e) => updateStyle('margin', e.target.value)}
            placeholder="8px 0"
          />
        </div>
      </div>
    </div>
  );
}